import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EligibilityPill } from "@/components/EligibilityPill";
import { MatchScore } from "@/components/MatchScore";
import { SearchPlanCard, MatchFunnel } from "@/components/OrchestratorPanel";
import { HumanAgencyNote } from "@/components/FitTransparency";
import { RemoteScopeBadge } from "@/components/RemoteScope";
import { formatSalary, locationLabel, arrangementLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise, Warning } from "@phosphor-icons/react";

export default function AiMatches() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const run = () => {
    setLoading(true);
    api.get("/matches/auto").then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { run(); }, []);

  const incomplete = data && data.profile_completed === false;

  return (
    <div>
      <PageHeader
        title="Your matches"
        subtitle="The engine turns your locked profile into searches automatically, filters by real eligibility, then ranks the criteria fit — not a hire probability. No keywords needed."
        action={<Button onClick={run} disabled={loading} data-testid="rerun-matches-btn" className="bg-slate-900 hover:bg-slate-800 gap-2"><ArrowsClockwise weight="bold" className={loading ? "animate-spin" : ""} /> {loading ? "Matching…" : "Refresh"}</Button>}
      />

      {!incomplete && <HumanAgencyNote className="mb-5" />}

      {incomplete && (
        <div className="flex items-start gap-3 rounded-md border border-orange-200 bg-orange-50 px-4 py-3" data-testid="matches-profile-incomplete">
          <Warning weight="fill" className="text-orange-600 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-orange-900">Complete your profile to generate matches</p>
            <p className="text-orange-800">The AI needs your locked constraints and background. <Link to="/profile" className="underline font-medium">Go to profile</Link></p>
          </div>
        </div>
      )}

      {loading && !data && <p className="text-sm text-slate-400" data-testid="matches-loading">Generating your search plan…</p>}

      {data && !incomplete && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 space-y-4">
            <SearchPlanCard plan={data.plan} kind="jobs" />
            <MatchFunnel funnel={data.funnel} kind="jobs" />
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="ai-matches-list">
              {data.matches.length === 0 && <p className="p-6 text-sm text-slate-500">No eligible matches from the generated searches yet. Widen your constraints or check your profile.</p>}
              {data.matches.map((j) => (
                <Link key={j.id} to={`/jobs/${j.id}`} className="block px-5 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors" data-testid={`ai-match-${j.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-900 truncate">{j.title}</p>
                      <p className="text-sm text-slate-600 truncate">{j.employer} · {locationLabel(j)} · {arrangementLabel(j.work_arrangement)}{formatSalary(j.salary_min, j.salary_max, j.salary_currency) ? " · " + formatSalary(j.salary_min, j.salary_max, j.salary_currency) : ""}</p>
                      <div className="mt-1"><RemoteScopeBadge summary={j.remote_summary} /></div>
                    </div>
                    <EligibilityPill status={j.eligibility_status} />
                    <OverallBadge overall={j.overall} />
                    <div className="w-14 text-right"><MatchScore score={j.match_score} /></div>
                  </div>
                  {j.matched_terms?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">{j.matched_terms.map((t, i) => <span key={i} className="text-[11px] bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 text-indigo-700">{t}</span>)}</div>
                  )}
                  {j.strong_matches?.length > 0 && <p className="mt-1.5 text-xs text-emerald-700 truncate">✓ {j.strong_matches.map((m) => m.text).join(", ")}</p>}
                  {j.missing_info?.length > 0 && <p className="mt-0.5 text-xs text-orange-700 truncate">Needs verification: {j.missing_info.join("; ")}</p>}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export function OverallBadge({ overall }) {
  if (!overall) return null;
  if (overall.score == null) {
    return <span data-testid="overall-badge" title={overall.why || overall.label}
      className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-400">— overall</span>;
  }
  const tone = overall.provisional
    ? "border-amber-200 bg-amber-50 text-amber-700"
    : "border-slate-900 bg-slate-900 text-white";
  return (
    <span data-testid="overall-badge" title={overall.why || overall.label}
      className={`inline-flex items-center gap-0.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold font-mono-num ${tone}`}>
      {overall.score}%{overall.provisional ? "*" : ""} <span className="font-sans font-normal opacity-80 ml-0.5">overall</span>
    </span>
  );
}
